import { sheets } from '@/app/grid/controller/Sheets';
import type { Sheet } from '@/app/grid/sheet/Sheet';
import * as gridSettings from '@/app/gridGL/pixiApp/gridSettings';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { rowHeadingWorldWidth } from '@/app/gridGL/UI/gridHeadings/rowHeadingMetrics';
import { embedInitialViewRange } from '@/app/helpers/isEmbed';
import { CELL_HEIGHT } from '@/shared/constants/gridConstants';
import { Point, type Rectangle } from 'pixi.js';

const MIN_SCALE = 0.01;
const MAX_SCALE = 10;
const MAX_ZOOM_IN = 2;
const SCALE_ITER_EPSILON = 1e-5;
const MAX_SCALE_ITERATIONS = 12;

let embedInitialViewApplied = false;

/** Max row index digit count that may appear in the vertical band used for the embed fit. */
function maxRowDigitsForEmbedFit(
  sheet: Sheet,
  worldRect: Rectangle,
  scale: number,
  drawableScreenHeight: number
): number {
  const halfWorldH = drawableScreenHeight / (2 * scale);
  const cy = worldRect.y + worldRect.height / 2;
  const yLow = Math.min(cy - halfWorldH, worldRect.y);
  const yHigh = Math.max(cy + halfWorldH, worldRect.bottom);
  const r1 = sheet.getRowFromScreen(yLow);
  const r2 = sheet.getRowFromScreen(Math.max(yLow + 1e-3, yHigh - 1e-3));
  const maxIdx = Math.max(1, r1, r2);
  return String(maxIdx).length;
}

/**
 * Whether the one-shot embed `initialView` fit has already been applied this session.
 */
export function isEmbedInitialViewApplied(): boolean {
  return embedInitialViewApplied;
}

/**
 * Fits the viewport to `embedInitialViewRange` once (instant, no animation).
 * Call from {@link Viewport.loadViewport} after restored position and clamp positioning.
 */
export function tryApplyEmbedInitialViewOnce(): boolean {
  if (embedInitialViewApplied || !embedInitialViewRange) {
    return false;
  }

  const range = embedInitialViewRange;
  const sheet = sheets.sheet;
  const worldRect = sheet.getWorldBoundsForEmbedRange(range);
  if (!worldRect || worldRect.width <= 0 || worldRect.height <= 0) {
    return false;
  }

  const viewport = pixiApp.viewport;
  const { screenWidth, screenHeight } = viewport;

  if (screenWidth <= 0 || screenHeight <= 0) {
    return false;
  }

  const headingsShown = gridSettings.showHeadings();
  const topHeadingScreen = headingsShown ? CELL_HEIGHT : 0;
  const drawableHeight = screenHeight - topHeadingScreen;
  if (drawableHeight <= 0) {
    return false;
  }

  // Row gutter width in world space grows when zooming out (see GridHeadings.drawVerticalBar). Using
  // headingSize from before we set scale under-reserves horizontal space and clips cell content.
  let scale = Math.min(screenWidth / (worldRect.width + rowHeadingWorldWidth(1, 3)), drawableHeight / worldRect.height);
  scale = Math.min(scale, MAX_ZOOM_IN);
  scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale));

  for (let i = 0; i < MAX_SCALE_ITERATIONS; i++) {
    const digitCount = headingsShown ? maxRowDigitsForEmbedFit(sheet, worldRect, scale, drawableHeight) : 1;
    const rowGutterWorld = headingsShown ? rowHeadingWorldWidth(scale, digitCount) : 0;
    const nextScale = Math.min(screenWidth / (worldRect.width + rowGutterWorld), drawableHeight / worldRect.height);
    const clamped = Math.max(MIN_SCALE, Math.min(MAX_SCALE, Math.min(nextScale, MAX_ZOOM_IN)));
    if (Math.abs(clamped - scale) < SCALE_ITER_EPSILON) {
      scale = clamped;
      break;
    }
    scale = clamped;
  }

  const digitCount = headingsShown ? maxRowDigitsForEmbedFit(sheet, worldRect, scale, drawableHeight) : 1;
  const rowHeadingWorld = headingsShown ? rowHeadingWorldWidth(scale, digitCount) : 0;
  const topHeadingWorld = topHeadingScreen > 0 ? topHeadingScreen / scale : 0;
  const center = new Point(
    worldRect.x + worldRect.width / 2 - rowHeadingWorld / 2,
    worldRect.y + worldRect.height / 2 - topHeadingWorld / 2
  );

  viewport.scale.set(scale, scale);
  viewport.moveCenter(center.x, center.y);
  viewport.dirty = true;

  embedInitialViewApplied = true;
  return true;
}
